import { MediaType } from "../../common/enums"
import logger from "../../common/logger"
import audioManager from "./audio-manager"

/**
 * 最开始的实现，上层应用获取媒体设备的devId，devId默认作为设备的mediaId
 * 但是，从浏览器获取的devId可能会非常长，长到超出FSP对mediaId长度的限制，
 * 在FireFox上会出现这种情况，为了兼容FireFox，需要对devId进行映射
 * 
 * 这样就会有三个ID：原始devId、映射后的devId、协议中的mediaId，关系如下：
 * 协议中的mediaId == 映射后的devId <=> 原始devId
 * 
 * 但切换设备后 mediaId !== 映射后的devId
 * 
 * 对于音频来说，又有特殊的地方，由于历史原因，默认所有端广播的麦克风，在
 * 协议中的mediaId都写死为“appdef_mic”，而且部分接收端写死只接收mediaId
 * 为“appdef_mic”的音频，因此，音频的三个ID关系如下：
 * 1）协议中的mediaId != 映射后的devId <=> 原始devId   
 * 
 * 为了打通这三者的关系，引入了curMicDev变量，用来标识当前正在使用哪个麦克风，
 * curMicDev是“映射后的devId”，这样三者关系如下：
 * "appdef_mic" => curMicDev <=> 原始devId
 * 
 * fixed 切换音频只支持 mediaId 不为 appdef_mic appdef_mic_magic的情况下切换
 * 
 * 你以为到此就结束了？- Too young, too simple，sometimes naive
 * Web端支持变声后，协议中的mediaId可能为“appdef_mic”和“appdef_mic_magic”
 * 则可能的关系如下：
 * "appdef_mic" => curMicDev <=> 原始devId
 * "appdef_mic_magic" => curMicDev <=> 原始devId
 */
class MediaMapping {
  constructor() {
    // Device ID mapping, firefox device ID including unexpected char
    // <mappingDevId, originDevId>
    this.videoInputMap = new Map();
    this.audioInputMap = new Map();
    this.audioOutputMap = new Map();
    // mediaId 与 devId 映射map
    this.videoMediaIdToDevIdMap = new Map();

    this.audioMediaIdToDevIdMap = new Map()
  }

  /**
   * 获取devId对应的mediaId
   * @param {*} mediaType 
   * @param {*} devId 
   */
  mappingDevIdToMediaId(mediaType, devId) {
    if (mediaType == MediaType.SCREEN_SHARE) {
      return devId;
    } else if (mediaType == MediaType.AUDIO) {
      return this._mappingAudioInputDevIdToMediaId(devId);
    } else if (mediaType == MediaType.VIDEO) {
      return this._mappingVideoInputDevIdToMediaId(devId);
    } else {
      logger.error("Invalid devType: " + mediaType);
      return null;
    }
  }
  /**
   * 切换设备时 修改mediaId 和 devId之间的映射
   * @param {*} mediaId
   * @param {*} toMediaId 
   */
  setMediaIdToDevIdMap (mediaType, mediaId, toMediaId) {
    if(!mediaId || !toMediaId) {
      logger.error('Invalid params')
      return
    }
    if (mediaType === MediaType.VIDEO) {
      if (!this.videoInputMap.get(toMediaId) || !this.videoInputMap.get(mediaId)) return logger.error('mediaId or switch mediaId is not exist')
      this.videoMediaIdToDevIdMap.set(mediaId, toMediaId)
    } else if (mediaType === MediaType.AUDIO) {
      if (!this.audioInputMap.get(toMediaId) || !this.audioInputMap.get(mediaId)) return logger.error('mediaId or switch mediaId is not exist')
      this.audioMediaIdToDevIdMap.set(mediaId, toMediaId)
    }
    
  }
  /**
   * 获取mediaId对应的devId
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  getMappingDevId(mediaType, mediaId) {
    if (mediaType == MediaType.SCREEN_SHARE) {
      return mediaId;
    } else if (mediaType == MediaType.AUDIO) {
      if (mediaId == 'appdef_mic' || mediaId == 'appdef_mic_magic') {
        return audioManager.getCurMicDevId();
      } else {
        return this._getAudioInputDevIdByMediaId(mediaId);
      }
    } else if (mediaType == MediaType.VIDEO) {
      return this._getVideoInputDevIdByMediaId(mediaId);
    } else {
      logger.error("Invalid devType: " + mediaType);
      return null;
    }
  }

  /**
   * mapping video input device Id in the form of 'videoinput_{index}'
   * @param {*} videoDevId 
   */
  _mappingVideoInputDevIdToMediaId(videoDevId) {
    let maxIndex = -1; // 保证mappingDevId不冲突
    // fixed bug #2390
    let cacheKey = -1
    this.videoInputMap.forEach(function(value, key) {
      if (value == videoDevId) {
        cacheKey = key
        return key;
      }
      if (key > maxIndex) {
        maxIndex = key;
      }
    });
    if (cacheKey !== -1) return cacheKey
    let mediaId = ++maxIndex + '';
    this.videoInputMap.set(mediaId, videoDevId);
    this.videoMediaIdToDevIdMap.set(mediaId, mediaId)
    return mediaId;
  }

  /**
   * 通过mediaId获取摄像头的devId
   * @param {*} mediaId 
   */
  _getVideoInputDevIdByMediaId(mediaId) {
    let devId = this.videoMediaIdToDevIdMap.get(mediaId)
    return this.videoInputMap.get(devId)
  }

  /**
   * mapping audio input device Id in the form of 'audioinput_{index}'
   * @param {*} audioDevId 
   */
  _mappingAudioInputDevIdToMediaId(audioDevId) {
    let maxIndex = -1; // 保证mappingDevId不冲突
    let cacheKey = -1
    this.audioInputMap.forEach(function(value, key) {
      if (value == audioDevId) { // 若存在 重新设置
        cacheKey = key
        return
      }
      if (key > maxIndex) {
        maxIndex = key;
      }
    });
    if (cacheKey !== -1) return cacheKey
    let mediaId = ++maxIndex + '';
    this.audioInputMap.set(mediaId, audioDevId);
    this.audioMediaIdToDevIdMap.set(mediaId, mediaId)
    return mediaId

  }

  /**
   * 通过mediaId获取麦克风devId
   * @param {*} mediaId 
   */
  _getAudioInputDevIdByMediaId(mediaId) {
    let devId = this.audioMediaIdToDevIdMap.get(mediaId)
    return this.audioInputMap.get(devId);
  }
}

export default new MediaMapping();